import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Maximize, Save, Download, ArrowLeft } from 'lucide-react';
import { useLibrary } from '../contexts/LibraryContext';
import { useApi } from '../contexts/ApiContext';
import { useAIOperation } from '../hooks/useAIOperation';
import { ProviderRegistry } from '../services/providerRegistry';
import { ReplicateProvider } from '../services/providers/ReplicateProvider';
import ImagePicker from '../components/ImagePicker';
import ProviderToggle from '../components/ProviderToggle';
import ImageCompare from '../components/ImageCompare';
import JobStatus, { CostBadge } from '../components/JobStatus';
import type { MedaImage } from '../types/image';

export default function UpscalePage() {
  const navigate = useNavigate();
  const { addImage } = useLibrary();
  const { tier, setTier, apiKeys } = useApi();
  const [opState, opActions] = useAIOperation();

  const [selectedImage, setSelectedImage] = useState<MedaImage | null>(null);
  const [scale, setScale] = useState<2 | 4>(2);
  const [faceEnhance, setFaceEnhance] = useState(false);
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);

  const registry = useMemo(() => new ProviderRegistry({
    huggingfaceKey: apiKeys.huggingface,
    replicateKey: apiKeys.replicate,
    preferredTier: tier,
  }), [apiKeys.huggingface, apiKeys.replicate, tier]);

  const provider = useMemo(() => registry.getBestProvider('upscale'), [registry]);
  const providers = useMemo(() => registry.getProvidersForOp('upscale'), [registry]);
  const hasFree = providers.some(p => p.info.tier === 'free');
  const hasPaid = providers.some(p => p.info.tier === 'paid');

  const estimatedCost = useMemo(() => {
    if (!provider) return 0;
    if (provider.info.tier === 'free') return 0;
    if (provider instanceof ReplicateProvider) return provider.getEstimatedCost('upscale');
    return 0.01;
  }, [provider]);

  const handleSelect = useCallback((image: MedaImage) => {
    setSelectedImage(image);
    if (originalUrl) URL.revokeObjectURL(originalUrl);
    setOriginalUrl(URL.createObjectURL(image.blob));
    opActions.reset();
  }, [originalUrl, opActions]);

  const handleUploadFile = useCallback(async (file: File) => {
    const img = await addImage(file, file.name);
    handleSelect(img);
  }, [addImage, handleSelect]);

  const handleUpscale = useCallback(async () => {
    if (!selectedImage || !provider) return;
    await opActions.run(provider, 'upscale', selectedImage.blob, {
      scale,
      face_enhance: faceEnhance,
    });
  }, [selectedImage, provider, opActions, scale, faceEnhance]);

  const handleSave = useCallback(async () => {
    if (!opState.resultBlob || !selectedImage) return;
    await addImage(
      opState.resultBlob,
      `${selectedImage.name} (${scale}x upscale)`,
      'generated',
      selectedImage.id,
      'upscale'
    );
    navigate('/library');
  }, [opState.resultBlob, selectedImage, scale, addImage, navigate]);

  const handleDownload = useCallback(() => {
    if (!opState.resultUrl || !selectedImage) return;
    const a = document.createElement('a');
    a.href = opState.resultUrl;
    a.download = `${selectedImage.name}_${scale}x.png`;
    a.click();
  }, [opState.resultUrl, selectedImage, scale]);

  const noProvider = !provider;
  const needsKey = noProvider && !apiKeys.huggingface && !apiKeys.replicate;

  return (
    <div className="flex flex-col h-full sage-animate-fade-in">
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 border-b"
        style={{ borderColor: 'var(--sage-white-5)', backgroundColor: 'var(--sage-sidebar)' }}
      >
        <button onClick={() => navigate('/')} className="p-1.5 rounded-lg" style={{ color: 'var(--sage-text-muted)' }}>
          <ArrowLeft size={18} />
        </button>
        <Maximize size={18} style={{ color: 'var(--sage-primary)' }} />
        <h1 className="text-sm font-medium flex-1">Upscale</h1>
        <ProviderToggle tier={tier} onToggle={setTier} freeAvailable={hasFree} paidAvailable={hasPaid} />
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left panel — controls */}
        <div
          className="w-72 p-4 border-r overflow-y-auto flex flex-col gap-5"
          style={{ borderColor: 'var(--sage-white-5)', backgroundColor: 'var(--sage-sidebar)' }}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--sage-text-muted)' }}>
              Select Image
            </p>
            <ImagePicker
              selectedId={selectedImage?.id ?? null}
              onSelect={handleSelect}
              onUpload={handleUploadFile}
            />
          </div>

          {selectedImage && (
            <>
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--sage-text-muted)' }}>
                  Scale Factor
                </p>
                <div className="flex gap-2">
                  {([2, 4] as const).map(s => (
                    <button
                      key={s}
                      onClick={() => setScale(s)}
                      className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: scale === s ? 'var(--sage-primary)' : 'var(--sage-bg)',
                        color: scale === s ? '#1c1b1a' : 'var(--sage-text-muted)',
                        border: `1px solid ${scale === s ? 'var(--sage-primary)' : 'var(--sage-white-10)'}`,
                      }}
                    >
                      {s}x
                    </button>
                  ))}
                </div>
                <p className="text-[10px] mt-2" style={{ color: 'var(--sage-text-muted)' }}>
                  {selectedImage.width}x{selectedImage.height} → {selectedImage.width * scale}x{selectedImage.height * scale}
                </p>
              </div>

              {tier === 'paid' && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={faceEnhance}
                    onChange={e => setFaceEnhance(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-xs" style={{ color: 'var(--sage-text)' }}>Enhance faces</span>
                </label>
              )}

              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--sage-text-muted)' }}>
                  Provider
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs" style={{ color: 'var(--sage-text)' }}>
                    {provider?.info.name || 'None configured'}
                  </span>
                  <CostBadge cost={estimatedCost} tier={provider?.info.tier || 'free'} />
                </div>
              </div>

              {needsKey ? (
                <div
                  className="p-3 rounded-lg text-xs"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--sage-accent) 10%, var(--sage-bg))', color: 'var(--sage-accent)' }}
                >
                  Add an API key in Settings to enable upscaling.
                  <button onClick={() => navigate('/settings')} className="underline ml-1">Open Settings</button>
                </div>
              ) : (
                <button
                  onClick={handleUpscale}
                  disabled={opState.status === 'submitting' || opState.status === 'processing'}
                  className="w-full py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  style={{ backgroundColor: 'var(--sage-primary)', color: '#1c1b1a' }}
                >
                  {opState.status === 'processing' ? 'Processing...' : `Upscale ${scale}x`}
                </button>
              )}

              <JobStatus
                status={opState.status}
                progress={opState.progress}
                error={opState.error}
                onRetry={handleUpscale}
              />

              {opState.status === 'succeeded' && (
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium"
                    style={{ backgroundColor: 'var(--sage-primary)', color: '#1c1b1a' }}
                  >
                    <Save size={14} /> Save to Library
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs"
                    style={{ backgroundColor: 'var(--sage-bg)', color: 'var(--sage-text-muted)', border: '1px solid var(--sage-white-10)' }}
                  >
                    <Download size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Main area — preview / comparison */}
        <div className="flex-1 flex items-center justify-center p-6 min-w-0">
          {!selectedImage ? (
            <div className="text-center">
              <Maximize size={48} style={{ color: 'var(--sage-white-10)' }} className="mx-auto mb-3" />
              <p className="text-sm" style={{ color: 'var(--sage-text-muted)' }}>
                Select an image from your library to upscale
              </p>
            </div>
          ) : opState.status === 'succeeded' && opState.resultUrl && originalUrl ? (
            <div className="w-full max-w-3xl">
              <ImageCompare
                beforeUrl={originalUrl}
                afterUrl={opState.resultUrl}
                beforeLabel="Original"
                afterLabel={`${scale}x Upscaled`}
              />
            </div>
          ) : (
            <div className="max-w-lg">
              <img
                src={originalUrl || ''}
                alt={selectedImage.name}
                className="rounded-xl shadow-lg max-h-[70vh] object-contain"
              />
              <p className="text-xs text-center mt-3" style={{ color: 'var(--sage-text-muted)' }}>
                {selectedImage.width}x{selectedImage.height}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
